import { useCallback, useRef, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import { woocommerceStoreService } from "../services/woocommerceStoreService";
import type {
  CustomerResolutionStatus,
  WooCustomer,
  WooStoreConfig,
} from "../types";
import { openFluxmedOpenIdLoginWindow } from "../utils/openIdLogin";
import { WordPressSiteOriginMissingError } from "../utils/wordpressSiteBase";

const ACCOUNT_LINK_POLL_INTERVAL_MS = 4000;
const MAX_ACCOUNT_LINKING_MS = 3 * 60 * 1000;

function mapCustomerError(err: unknown): string {
  return err instanceof Error
    ? err.message
    : "Não foi possível verificar seu cadastro na loja.";
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function tryClosePopup(popup: Window | null): void {
  if (!popup || popup.closed) return;
  try {
    popup.close();
  } catch {
    // cross-origin — ignorar
  }
}

/**
 * Resolve customer WooCommerce por e-mail do paciente.
 * Sempre busca por e-mail — se o cadastro foi removido na loja, exige nova associação.
 */
export function useWooCommerceCustomer(
  store: WooStoreConfig | null,
  userEmail: string,
) {
  const [customer, setCustomer] = useState<WooCustomer | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [status, setStatus] = useState<CustomerResolutionStatus>("idle");
  const [isLinkingAccount, setIsLinkingAccount] = useState(false);
  const linkCancelRef = useRef(false);
  const linkPopupRef = useRef<Window | null>(null);

  const resolveTask = useCallback(
    async (params: { store: WooStoreConfig; email: string }) => {
      const { store: activeStore, email } = params;
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) {
        throw new Error("E-mail do usuário não disponível para associar à loja.");
      }

      return woocommerceStoreService.findCustomerByEmail(
        activeStore,
        normalizedEmail,
      );
    },
    [],
  );

  const { run: runResolve, loading, error } = useAsyncTask({
    task: resolveTask,
    mapError: mapCustomerError,
  });

  const resolveCustomer = useCallback(async () => {
    if (!store || !userEmail.trim()) {
      setStatus("error");
      return null;
    }

    setStatus("loading");
    try {
      const result = await runResolve({ store, email: userEmail });
      if (result) {
        setCustomer(result);
        setCustomerId(result.id);
        setStatus("found");
        return result;
      }
      setCustomer(null);
      setCustomerId(null);
      setStatus("login_required");
      return null;
    } catch {
      setStatus("error");
      return null;
    }
  }, [store, userEmail, runResolve]);

  const cancelLinkAccount = useCallback(() => {
    linkCancelRef.current = true;
    tryClosePopup(linkPopupRef.current);
    linkPopupRef.current = null;
    setIsLinkingAccount(false);
  }, []);

  const linkAccount = useCallback(async (): Promise<
    WooCustomer | "cancelled" | null
  > => {
    if (!store || !userEmail.trim()) return null;

    if (!store.siteOrigin?.trim()) {
      throw new WordPressSiteOriginMissingError(store.name);
    }

    linkCancelRef.current = false;
    setIsLinkingAccount(true);

    const normalizedEmail = userEmail.trim().toLowerCase();
    const popup = openFluxmedOpenIdLoginWindow(store);
    linkPopupRef.current = popup;

    const startedAt = Date.now();

    try {
      while (!linkCancelRef.current) {
        if (Date.now() - startedAt >= MAX_ACCOUNT_LINKING_MS) {
          return null;
        }

        try {
          const result = await woocommerceStoreService.findCustomerByEmail(
            store,
            normalizedEmail,
          );
          if (result) {
            setCustomer(result);
            setCustomerId(result.id);
            setStatus("found");
            return result;
          }
        } catch {
          // continua polling
        }

        await sleep(ACCOUNT_LINK_POLL_INTERVAL_MS);
      }

      return "cancelled";
    } finally {
      tryClosePopup(linkPopupRef.current);
      linkPopupRef.current = null;
      setIsLinkingAccount(false);
    }
  }, [store, userEmail]);

  /** @deprecated Preferir linkAccount — mantido para compatibilidade interna. */
  const handleOpenIdLogin = linkAccount;

  const refreshCustomer = useCallback(async () => {
    return resolveCustomer();
  }, [resolveCustomer]);

  const resetCustomer = useCallback(() => {
    cancelLinkAccount();
    setCustomer(null);
    setCustomerId(null);
    setStatus("idle");
  }, [cancelLinkAccount]);

  return {
    customer,
    customerId,
    status,
    loading,
    isLinkingAccount,
    error: error ?? null,
    resolveCustomer,
    linkAccount,
    cancelLinkAccount,
    handleOpenIdLogin,
    refreshCustomer,
    resetCustomer,
  };
}
